// docFolders.js — document upload folder helpers.
//
// There is no folder taxonomy in the app. A deal's structure is generated by
// Claude from its documents and served, merged with what the deal really has,
// by GET /api/document-folders (see useDocumentFolders). These helpers turn
// that flat list into something a picker can render, and validate a new folder
// name before we ask the server — and SharePoint — to create it.
//
// Mirrors the backend's api/src/documentFolders.js — keep the two in lockstep.

const INVALID_SEGMENT_CHARS = /["*:<>?\\|/]/;
export const MAX_FOLDER_DEPTH   = 8;
const MAX_SEGMENT_LENGTH = 100;
const MAX_PATH_LENGTH    = 380;
const RESERVED_SEGMENTS  = new Set(['.', '..', 'forms', '_vti_']);

// Where a merged folder came from — drives the little source chip in the UI.
export const FOLDER_SOURCE_LABEL = {
  ai:         'AI suggested',
  custom:     'Added in Harvi',
  sharepoint: 'In SharePoint',
  documents:  'In use',
};

export function normalizeFolderPath(input) {
  return String(input === null || input === undefined ? '' : input)
    .replace(/\\/g, '/')
    .split('/')
    .map(s => s.trim())
    .filter(Boolean)
    .join('/');
}

// Why a folder path is unusable, or null when it's fine. Same rules (and the
// same messages) as the API, so the inline form can reject before a round-trip.
export function folderPathError(input) {
  const path = normalizeFolderPath(input);
  if (!path) return 'Enter a folder name.';
  if (path.length > MAX_PATH_LENGTH) return `Folder path is too long (max ${MAX_PATH_LENGTH} characters).`;
  const segments = path.split('/');
  if (segments.length > MAX_FOLDER_DEPTH) return `Folders can be nested at most ${MAX_FOLDER_DEPTH} levels deep.`;
  for (const seg of segments) {
    if (seg.length > MAX_SEGMENT_LENGTH) return `"${seg.slice(0, 30)}…" is too long (max ${MAX_SEGMENT_LENGTH} characters).`;
    if (INVALID_SEGMENT_CHARS.test(seg)) return `A folder name cannot contain " * : < > ? \\ | — check "${seg}".`;
    if (RESERVED_SEGMENTS.has(seg.toLowerCase())) return `"${seg}" is a reserved folder name.`;
    if (seg.endsWith('.')) return `A folder name cannot end with a period — check "${seg}".`;
  }
  return null;
}

export function folderLabel(path) {
  const norm = normalizeFolderPath(path);
  if (!norm) return '';
  const parts = norm.split('/');
  return parts[parts.length - 1];
}

// Nest a flat merged folder list. A segment nobody declared as a folder in its
// own right (the "Due Diligence" above "Due Diligence/Harvus/4 - Reports")
// becomes a non-selectable group header — which is how a hierarchy that grew in
// SharePoint renders without the app knowing its shape in advance.
export function buildFolderTree(folders = []) {
  const root = { children: [], index: new Map() };

  const nodeFor = (parent, name, path) => {
    const key = name.toLowerCase();
    let node = parent.index.get(key);
    if (!node) {
      node = {
        name, path, label: name,
        selectable: false, requiresYear: false, hint: null,
        source: null, docCount: 0, totalDocCount: 0,
        children: [], index: new Map(),
      };
      parent.index.set(key, node);
      parent.children.push(node);
    }
    return node;
  };

  for (const f of folders) {
    const segments = normalizeFolderPath(f.path).split('/').filter(Boolean);
    let parent = root;
    let acc = '';
    for (let i = 0; i < segments.length; i++) {
      acc = acc ? `${acc}/${segments[i]}` : segments[i];
      const node = nodeFor(parent, segments[i], acc);
      node.totalDocCount += f.docCount || 0;
      if (i === segments.length - 1) {
        node.selectable   = true;
        node.label        = f.label || node.label;
        node.requiresYear = !!f.requiresYear;
        node.hint         = f.hint || null;
        node.source       = f.source || null;
        node.docCount     = f.docCount || 0;
      }
      parent = node;
    }
  }

  const strip = (nodes) => nodes.map(({ index: _index, children, ...rest }) => ({ ...rest, children: strip(children) }));
  return strip(root.children);
}

// ─── Upload entry collection helpers ────────────────────────────────────────
// Both helpers return Array<{ file: File, relativePath: string }>.
// relativePath is the subfolder path relative to the drop target ('' for top-level files).
// Hidden files (names starting with '.') are filtered out at every level.

// For <input type="file" multiple> and <input type="file" webkitdirectory>.
// Files from webkitdirectory inputs carry file.webkitRelativePath like "FolderName/SubDir/file.pdf".
export function collectEntriesFromInputFiles(fileList) {
  return Array.from(fileList)
    .filter(f => !f.name.startsWith('.'))
    .map(f => {
      const parts = (f.webkitRelativePath || '').split('/');
      // e.g. ['FolderName', 'SubDir', 'file.pdf'] → relativePath = 'FolderName/SubDir'
      const relativePath = parts.length > 1 ? parts.slice(0, -1).join('/') : '';
      return { file: f, relativePath };
    });
}

// For drag-and-drop using the File System Entry API.
// Traverses directories recursively; loops readEntries() to handle >100-entry directories.
export async function collectEntriesFromDataTransfer(dataTransfer) {
  async function readAll(reader) {
    const all = [];
    await new Promise(resolve => {
      function next() {
        reader.readEntries(batch => {
          if (!batch.length) { resolve(); return; }
          all.push(...batch);
          next();
        });
      }
      next();
    });
    return all;
  }

  async function traverse(entry, pathPrefix, results) {
    if (entry.name.startsWith('.')) return;
    if (entry.isFile) {
      const file = await new Promise((res, rej) => entry.file(res, rej));
      results.push({ file, relativePath: pathPrefix });
    } else if (entry.isDirectory) {
      const childPrefix = pathPrefix ? `${pathPrefix}/${entry.name}` : entry.name;
      const children = await readAll(entry.createReader());
      for (const child of children) await traverse(child, childPrefix, results);
    }
  }

  const items = Array.from(dataTransfer.items || []);
  if (!items.length || !items[0].webkitGetAsEntry) {
    return collectEntriesFromInputFiles(dataTransfer.files);
  }
  // The DataTransfer is only valid synchronously during the drop event — its item
  // list (and .files) is neutered after the first `await`. So resolve EVERY entry,
  // and snapshot the plain File list, up front. (Previously webkitGetAsEntry() was
  // called lazily inside an awaiting loop, so all items after the first came back
  // null and only one dropped file was ever collected.)
  const entries = items.map(item => item.webkitGetAsEntry()).filter(Boolean);
  const fallbackFiles = collectEntriesFromInputFiles(dataTransfer.files);
  const results = [];
  for (const entry of entries) await traverse(entry, '', results);
  return results.length ? results : fallbackFiles;
}
